import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import UserSelect from '~/components/Shared/UserSelect/UserSelect';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { Nullable } from '~/eds/src/types/Nullable';
import { PilotId } from '~/eds/src/types/PilotId';
import { User } from '~/eds/src/types/User';
import { getUserName } from '~/eds/src/utils/getUserName';
import { withUsers } from '~/hocs/withUsers';
import { Option } from '~/types/Option';

interface Props {
  users: Record<number, User>;
  setRecipient: (recipient: {
    email?: string;
    name?: string;
    userId?: number;
  }) => void;
  internalRecipientIds: [number];
}

const UserEmailSearch = ({
  users,
  internalRecipientIds,
  setRecipient,
}: Props) => {
  const intl = useIntl();
  const [newCcId, setNewCcId] = useState<Nullable<number>>(null);
  const [newCCEmail, setNewCCEmail] = useState('');
  const [newCCName, setNewCCName] = useState('');

  const handleOnAddRecipient = (updatedValues: Option<string>) => {
    setNewCcId(null);
    setNewCCName('');
    setNewCCEmail(updatedValues.value);
  };

  useEffect(() => {
    setRecipient({
      email: newCCEmail,
      name: newCCName,
      userId: newCcId ?? undefined,
    });
  }, [newCCName, newCcId, newCCEmail]);

  const onUserSelect = (id: Nullable<PilotId>) => {
    if (id) {
      const user = users[id as number];
      if (user) {
        setNewCcId(id as number);
        const name = getUserName({
          user,
          intl,
        });
        setNewCCName(name);
      }
    } else {
      setNewCcId(null);
      setNewCCName('');
      setNewCCEmail('');
    }
  };

  const onNameChange = (updatedValue: Nullable<string>) => {
    setNewCCName(updatedValue || '');
  };

  return (
    <Layout direction="column" spacing={8} w="100%">
      <Layout direction="column" space={4}>
        <Label htmlFor="cc-email">
          {intl.formatMessage({
            id: 'UserEmailSearch.emailLabel',
            defaultMessage: 'Email',
            description: 'Label for email field',
          })}
        </Label>
        <UserSelect
          notAvailableUsers={internalRecipientIds}
          isMulti={false}
          name="cc-email"
          value={newCcId || newCCEmail}
          placeholder={intl.formatMessage({
            id: 'UserEmailSearch.userPlaceholder',
            defaultMessage: 'Add user...',
            description: 'Placeholder text for user selection',
          })}
          onChange={onUserSelect}
          onAddOption={handleOnAddRecipient}
          enablePortal={true}
        />
      </Layout>
      <Layout direction="column" space={4}>
        <Label htmlFor="cc-name">
          {intl.formatMessage({
            id: 'UserEmailSearch.nameLabel',
            defaultMessage: 'Name',
            description: 'Label for name field',
          })}
        </Label>
        <TextInput
          name="cc-name"
          id="recipient-set-name"
          disabled={!!newCcId || !newCCEmail}
          value={newCCName}
          onChange={onNameChange}
        />
      </Layout>
    </Layout>
  );
};
export default withUsers(UserEmailSearch);
